<template>
  <div class="hidden">
    <StoreFinderMapInfoText
      ref="infoWindowContent"
      :store="markerData.store"
      :map="map"
      @click:link="onClick"
    />
  </div>
</template>

<script setup lang="ts">
import type { Loader } from '@googlemaps/js-api-loader'
import StoreFinderMapInfoText from '~/components/store-finder/StoreFinderMapInfoText.vue'
import { useMapInfoWindow } from '~/composables/useMapInfoWindow'
import { useMapMarker, type MarkerOptions } from '~/composables/useMapMarker'

type Map = google.maps.Map
type MarkerEventListener = google.maps.MapsEventListener

const props = defineProps({
  loader: {
    type: Object as PropType<Loader>,
    required: true,
  },
  map: {
    type: Object as PropType<Map>,
    required: true,
  },
  markerData: {
    type: Object as PropType<MarkerOptions>,
    required: true,
  },
})

const emit = defineEmits<{
  (e: 'click:link', value: MouseEvent): void
}>()

const onClick = (e: MouseEvent) => {
  emit('click:link', e)
}

const scope = effectScope()
onScopeDispose(() => scope.stop())

const { Size, SymbolPath } = await props.loader.importLibrary('core')
const { Marker } = await props.loader.importLibrary('marker')

const defaultMarkerIcon = {
  path: SymbolPath.CIRCLE,
  scale: 6,
  strokeColor: '#000',
  strokeOpacity: 0.9,
  strokeWeight: 1.5,
  fillColor: '#000',
  fillOpacity: 0.4,
}

const activeMarkerIcon = {
  url: '/icons/map_pin_dropshadow.svg',
  scaledSize: new Size(40, 40),
  anchor: new google.maps.Point(20, 20),
}

const markerEventListener = ref<MarkerEventListener>()
const markerIcon = computed(() => {
  return activeMarkerIcon
})
const marker = new Marker({
  map: props.map,
  icon: markerIcon.value,
  ...props.markerData,
})

const { infoWindow, newInfoWindow, currentStore, setCurrentStore } =
  useMapInfoWindow()
const { latLngBounds, setLatLngBounds, mapsLoader } = useMapMarker()

const infoWindowEventListener = ref<MarkerEventListener>()
const infoWindowContent = ref()

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth',
  })
}

const showInfoView = () => {
  if (!infoWindow.value) {
    newInfoWindow(google)
  }

  infoWindow.value?.setContent(infoWindowContent.value?.$el)
  infoWindow.value?.open({
    anchor: marker,
    map: props.map,
    shouldFocus: false,
  })

  setCurrentStore(props.markerData.store)
  scrollTo(props.markerData.anchor)

  infoWindowEventListener.value = infoWindow.value?.addListener(
    'closeclick',
    () => {
      setCurrentStore(undefined)
    },
  )
}

onBeforeMount(() => {
  if (!mapsLoader.value) {
    setLatLngBounds(props.loader)
  }
})

onMounted(() => {
  const position = props.markerData.position
  if (position && latLngBounds.value) {
    latLngBounds.value.extend(position)
    props.map.setCenter(latLngBounds.value.getCenter())
    props.map.fitBounds(latLngBounds.value)
  }

  marker.setIcon(activeMarkerIcon)

  markerEventListener.value = marker.addListener('click', () => {
    if (!props.markerData.noClick) {
      showInfoView()
    }
  })
})

onUnmounted(() => {
  markerEventListener.value?.remove()
  infoWindowEventListener.value?.remove()
  marker.setMap(null)
})

const defaultZIndex = ref<number | null | undefined>(undefined)

scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch(currentStore, (value) => {
    if (value?.id === props.markerData.store.id) {
      showInfoView()
      if (!defaultZIndex.value && marker.getZIndex()) {
        defaultZIndex.value = marker.getZIndex()
      }
      marker.setZIndex(google.maps.Marker.MAX_ZINDEX)
      marker.setIcon(activeMarkerIcon)
    } else {
      marker.setIcon(defaultMarkerIcon)
      marker.setZIndex(defaultZIndex.value)
    }
  })
})
</script>
